import React, { useEffect, useRef } from 'react';
import Truncate from 'react-truncate';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { getSrc } from 'gatsby-plugin-image';
import { Trans, Link, useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import { DISCOUNT_AMOUNT, PRICE_THRESHOLD } from '../../constants/pricing';

interface Product {
  title: string;
  handle: string;
  variants: any[];
  priceRangeV2: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
}

interface ProductCard {
  product: Product;
}

export const ProductCard: React.FunctionComponent<ProductCard> = ({ product }) => {
  const {
    handle,
    priceRangeV2: { minVariantPrice, maxVariantPrice },
  } = product;
  const isMinimum = minVariantPrice.amount !== maxVariantPrice.amount;
  const price = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(
    parseFloat(minVariantPrice.amount) > PRICE_THRESHOLD
      ? parseFloat(minVariantPrice.amount) - DISCOUNT_AMOUNT
      : parseFloat(minVariantPrice.amount)
  );

  const handleArr = handle.split('-');
  const newHandle = handleArr[0] + '-' + handleArr[1] + '-' + handleArr[2] + '-' + handleArr[3];
  const { t } = useTranslation();
  const { language } = useI18next();

  const cardRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!cardRef.current || !window.IntersectionObserver) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && imgRef.current) {
            // 提升加载优先级
            imgRef.current.loading = 'eager';
            imgRef.current.fetchPriority = 'high';

            // 提前解码图片
            imgRef.current.decode().catch(() => {
              console.warn('图片预加载失败:', product.title);
            });

            // 停止观察
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '200px 0px', // 提前1000px预加载
        threshold: 0.01,
      }
    );

    observer.observe(cardRef.current);

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [product.title]);

  return (
    <Card
      ref={cardRef}
      className="card"
      sx={{
        m: '5px',
        minHeight: {
          xs: 'calc(100vw / 2 - 15px + 60px)',
          sm: 'calc(100vw / 3 - 15px + 70px)',
          md: 'calc(100vw / 4 - 15px + 80px)',
          lg: '390px',
        },
      }}>
      <CardActionArea
        href={`/${newHandle}`}
        id={`card-${newHandle}`}
        onClick={() => {
          // fire GTM view_item_list event
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'select_item',
            ecommerce: {
              item_list_name: window.location.pathname + window.location.search,
              item_list_id: `${product.title.split(' ')[2]}`,
              items: [
                {
                  item_id: `${product.title.split(' ')[2]}`,
                  item_name: window.location.pathname + window.location.search,
                },
              ],
            },
          });
          // alert("fire view_item_list event");
        }}>
        <Link
          to={`/${newHandle}`}
          language={language}
          id={`viewCard-${newHandle}`}
          style={{ textDecoration: 'none', fontSize: '14px', fontWeight: '400', color: '#363636' }}>
          <CardMedia
            ref={imgRef}
            id={`cardMedia-${newHandle}`}
            component="img"
            image={getSrc(product.variants[0].image.gatsbyImageData)}
            alt={t(product.title)}
            loading="eager" // 改为主动加载
            decoding="async"
            fetchpriority="high" // 添加加载优先级
            sx={{
              width: '100%',
              aspectRatio: '1/1', // 正方形占位
              margin: 0,
              borderRadius: '4px',
              filter: 'brightness(96%)',
              contentVisibility: 'auto', // 添加CSS内容可见性优化
            }}
          />
          <CardContent
            id={`cardContent-${newHandle}`}
            sx={{
              '& .MuiCardContent-root:last-child': {
                paddingBottom: '0px',
              },
              width: '100%',
              px: '5px',
              py: '5px',
              textAlign: 'left',
            }}>
            <Typography variant="body2">
              <Truncate lines={2} ellipsis={<span>... </span>}>
                {t(product.title)}
              </Truncate>
            </Typography>
            <Typography variant="body2" sx={{ pt: '4px' }}>
              {`${isMinimum ? 'from ' : ''}${price}`}
              {`  `}
              <Trans>EU VAT included</Trans>
            </Typography>
          </CardContent>
        </Link>
      </CardActionArea>
    </Card>
  );
};
